import { useCallback, useEffect, useRef, useState } from "react";
import { buildIdFetcher } from "./useBuildIdCheck/fetcher";
import { buildIdSimulator } from "./useBuildIdCheck/simulator";
import { buildIdStorage } from "./useBuildIdCheck/storage";
import { buildIdValidator } from "./useBuildIdCheck/validator";

/**
 * Build ID check configuration
 */
export interface BuildIdCheckOptions {
  /** Check interval in milliseconds, default 30 minutes */
  checkInterval?: number;
  /** Whether to check immediately on component mount, default true */
  checkOnMount?: boolean;
  /** Whether to enable automatic checking, default true */
  autoCheck?: boolean;
  /** Whether to enable page visibility change detection, default true */
  checkOnVisibilityChange?: boolean;
}

/**
 * Build ID check state
 */
export interface BuildIdCheckState {
  /** Whether currently checking */
  isChecking: boolean;
  /** Whether there is an update available */
  hasUpdate: boolean;
  /** Current build ID */
  currentBuildId: string | null;
  /** Latest build ID */
  latestBuildId: string | null;
  /** Error message */
  error: string | null;
  /** Last check time */
  lastCheckTime: Date | null;
}

/**
 * Build ID check Hook
 */
export function useBuildIdCheck(options: BuildIdCheckOptions = {}) {
  const {
    checkInterval = 30 * 60 * 1000, // Default 30 minutes
    checkOnMount = true,
    autoCheck = true,
    checkOnVisibilityChange = true,
  } = options;

  const [state, setState] = useState<BuildIdCheckState>({
    isChecking: false,
    hasUpdate: false,
    currentBuildId: null,
    latestBuildId: null,
    error: null,
    lastCheckTime: null,
  });

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Get current build ID
   */
  const getCurrentBuildId = useCallback(() => {
    return buildIdStorage.getCurrentBuildId();
  }, []);

  /**
   * Set current build ID
   */
  const setCurrentBuildId = useCallback((buildId: string) => {
    const normalizedBuildId = buildIdValidator.normalize(buildId);
    if (!normalizedBuildId) {
      console.warn("Invalid build ID detected, not storing:", buildId.substring(0, 100));
      return;
    }

    buildIdStorage.setCurrentBuildId(normalizedBuildId);
    setState((prev) => ({ ...prev, currentBuildId: normalizedBuildId }));
  }, []);

  /**
   * Fetch latest build ID from server
   */
  const fetchLatestBuildId = useCallback(async (): Promise<string> => {
    // Check for simulation in development
    if (buildIdSimulator.isSimulationEnabled()) {
      const simulatedBuildId = buildIdSimulator.getSimulatedBuildId();
      if (simulatedBuildId) {
        console.log("fetchLatestBuildId - returning simulated update ID:", simulatedBuildId);
        return simulatedBuildId;
      }
    }

    // Use the modular fetcher
    return await buildIdFetcher.fetchLatestBuildId();
  }, []);

  /**
   * Execute build ID check
   */
  const checkBuildId = useCallback(async () => {
    setState((prev) => ({ ...prev, isChecking: true, error: null }));

    try {
      const currentBuildId = getCurrentBuildId();
      const latestBuildId = await fetchLatestBuildId();

      // Validate the received buildId
      if (!buildIdValidator.isValidBuildId(latestBuildId)) {
        console.warn("Invalid build ID received from server:", latestBuildId);
        setState((prev) => ({
          ...prev,
          isChecking: false,
          error: "Invalid build ID received",
          lastCheckTime: new Date(),
        }));
        return { hasUpdate: false, currentBuildId, latestBuildId: null };
      }

      // If first visit, save current build ID
      if (!currentBuildId) {
        setCurrentBuildId(latestBuildId);
        setState((prev) => ({
          ...prev,
          isChecking: false,
          currentBuildId: latestBuildId,
          latestBuildId,
          hasUpdate: false,
          lastCheckTime: new Date(),
        }));
        return { hasUpdate: false, currentBuildId: latestBuildId, latestBuildId };
      }

      // Compare build IDs
      const hasUpdate = buildIdValidator.isDifferent(currentBuildId, latestBuildId);

      // Log when updates are detected
      if (hasUpdate) {
        console.log(`Build ID changed: ${currentBuildId} -> ${latestBuildId}`);
      }

      setState((prev) => ({
        ...prev,
        isChecking: false,
        currentBuildId,
        latestBuildId,
        hasUpdate,
        lastCheckTime: new Date(),
      }));

      return { hasUpdate, currentBuildId, latestBuildId };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Version check failed";
      console.error("Version check failed:", errorMessage);
      setState((prev) => ({
        ...prev,
        isChecking: false,
        error: errorMessage,
      }));

      // Add retry mechanism
      if (navigator.onLine) {
        console.log("Network is online, scheduling retry in 30 seconds");
        setTimeout(() => {
          console.log("Retrying version check...");
          checkBuildId().catch(console.error);
        }, 30000); // Retry after 30 seconds
      }

      throw error;
    }
  }, [getCurrentBuildId, fetchLatestBuildId, setCurrentBuildId]);

  /**
   * Update to latest version
   */
  const updateToLatest = useCallback(async () => {
    if (state.latestBuildId) {
      // Clear simulation flags in development
      if (import.meta.env.DEV) {
        buildIdSimulator.disableSimulation();
        console.log("updateToLatest - cleared development simulation flags");
      }

      setCurrentBuildId(state.latestBuildId);
      setState((prev) => ({
        ...prev,
        hasUpdate: false,
        currentBuildId: state.latestBuildId,
      }));

      console.log("updateToLatest - updated to latest version:", state.latestBuildId);
    }
  }, [state.latestBuildId, setCurrentBuildId]);

  /**
   * Clear timer
   */
  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /**
   * Start automatic checking
   */
  const startAutoCheck = useCallback(() => {
    if (!autoCheck) return;

    clearTimer();
    intervalRef.current = setInterval(() => {
      checkBuildId().catch(console.error);
    }, checkInterval);
  }, [autoCheck, checkInterval, checkBuildId, clearTimer]);

  /**
   * Stop automatic checking
   */
  const stopAutoCheck = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  /**
   * Simulate version update in development (for testing only)
   */
  const simulateUpdate = useCallback(() => {
    if (import.meta.env.DEV) {
      console.log("simulateUpdate - starting version update simulation");
      buildIdSimulator.enableSimulation();
      // Trigger immediate check
      checkBuildId().catch(console.error);
    }
  }, [checkBuildId]);

  /**
   * Reset simulation state in development (for testing only)
   */
  const resetSimulation = useCallback(() => {
    if (import.meta.env.DEV) {
      console.log("resetSimulation - resetting simulation state");
      buildIdSimulator.disableSimulation();
      buildIdStorage.clearBuildId();

      // Reset state
      setState((prev) => ({
        ...prev,
        hasUpdate: false,
        currentBuildId: null,
        latestBuildId: null,
      }));

      // Re-check
      checkBuildId().catch(console.error);
    }
  }, [checkBuildId]);

  // Initialization
  useEffect(() => {
    // Set initial current build ID
    const currentBuildId = getCurrentBuildId();
    if (currentBuildId) {
      setState((prev) => ({ ...prev, currentBuildId }));
    }

    // Initial check
    if (checkOnMount) {
      checkBuildId().catch(console.error);
    }

    // Start automatic checking
    if (autoCheck) {
      startAutoCheck();
    }

    return () => {
      clearTimer();
    };
  }, [checkOnMount, autoCheck, startAutoCheck, clearTimer, checkBuildId, getCurrentBuildId]);

  // Page visibility change detection
  useEffect(() => {
    if (!checkOnVisibilityChange) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // When page becomes visible again, re-check if time since last check exceeds interval
        if (state.lastCheckTime) {
          const timeSinceLastCheck = Date.now() - state.lastCheckTime.getTime();
          if (timeSinceLastCheck >= checkInterval) {
            checkBuildId().catch(console.error);
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkOnVisibilityChange, checkInterval, state.lastCheckTime, checkBuildId]);

  return {
    ...state,
    checkBuildId,
    updateToLatest,
    startAutoCheck,
    stopAutoCheck,
    // Development testing functions
    ...(import.meta.env.DEV && {
      simulateUpdate,
      resetSimulation,
    }),
  };
}
